import { Box, Button, Flex, Group, Text, ThemeIcon } from '@mantine/core';
import { DialogResourceCopyProps } from './types';
import { Trans, useTranslation } from 'react-i18next';
import {
  useCopyResourcesMutation,
  useBulkMoveResourcesMutation,
} from '@app/types/blackroom-api';
import LIST_RESOURCES from '../api/listResources';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';
import { showNotification } from '@mantine/notifications';
import { ApolloError, useReactiveVar } from '@apollo/client';
import dialogStoreVar from '@features/Dialog/store/dialogStore';
import {
  clearCopiedResourceIds,
  resourceClipboardVar,
} from '../store/copiedResources';
import { IconClipboard, IconCut } from '@tabler/icons-react';
import { palette } from '@app/shared/ui-kit/colors';
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';

function DialogResourceCopy({
  destinationFolderId = null,
  requestVariables,
}: DialogResourceCopyProps) {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const resourceClipboard = useReactiveVar(resourceClipboardVar);

  const [copyResources, { loading: copyLoading }] = useCopyResourcesMutation();
  const [moveResources, { loading: moveLoading }] =
    useBulkMoveResourcesMutation();

  const loading = copyLoading || moveLoading;
  const isCutOperation = resourceClipboard.operation === 'cut';

  const onSubmit = async () => {
    try {
      if (isCutOperation) {
        // Move resources (cut operation)
        await moveResources({
          awaitRefetchQueries: true,
          variables: {
            resourcesToMove: resourceClipboard.resourceIds,
            to: destinationFolderId,
          },
          update(cache) {
            resourceClipboard.resourceIds.forEach((id) => {
              cache.evict({
                id: cache.identify({ __typename: 'Resource', id }),
                fieldName: 'path',
              });
            });
            cache.gc();
          },
          refetchQueries: [
            {
              query: LIST_RESOURCES,
              variables: requestVariables,
            },
          ],
        });
      } else {
        // Copy resources (copy operation)
        await copyResources({
          awaitRefetchQueries: true,
          variables: {
            copyInput: {
              resourceIds: resourceClipboard.resourceIds,
              parentId: destinationFolderId,
              workspaceId: workspace.id,
            },
          },
          refetchQueries: [
            {
              query: LIST_RESOURCES,
              variables: requestVariables,
            },
          ],
        });
      }

      // Show success notification
      showNotification({
        title: t('Common:ack.success.genericTitle'),
        message: t('Common:ack.success.genericMessage'),
        color: 'blackroomGreen',
      });

      // Clear the clipboard after successful paste
      clearCopiedResourceIds();

      dialogStore?.closeDialog?.('dialog-resource-copy');
    } catch (error) {
      // Handle thrown Apollo errors
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });

      if (error instanceof ApolloError) {
        console.error(
          `Apollo error ${isCutOperation ? 'moving' : 'copying'} resources:`,
          {
            graphQLErrors: error.graphQLErrors,
            networkError: error.networkError,
            message: error.message,
          },
        );
      } else {
        console.error(
          `Error ${isCutOperation ? 'moving' : 'copying'} resources:`,
          error,
        );
      }
    }
  };

  return (
    <Flex direction="column" maw="500px">
      <Box bg="blackroomBlue" h={4} w="100%" mb="md" />

      <Flex direction="column" align="center" gap="md" px="xl" pb="md">
        <ThemeIcon size={80} radius="50%" variant="light" color="blackroomBlue">
          {isCutOperation ? (
            <IconCut size={40} stroke={1.5} />
          ) : (
            <IconClipboard size={40} stroke={1.5} />
          )}
        </ThemeIcon>

        <Text
          size="lg"
          ta="center"
          fw={FONT_WEIGHT.bold}
          c={palette.blackroomDarkGray2}
          maw="420px"
        >
          <Trans
            i18nKey={
              isCutOperation
                ? 'Resource:Common.notifications.resourcesPasteMove.confirmMessage'
                : 'Resource:Common.notifications.resourcesPaste.confirmMessage'
            }
            count={resourceClipboard.resourceIds.length}
            components={{ strong: <strong /> }}
          />
        </Text>
      </Flex>

      <Group h="100%" justify="flex-end" align="center" pt="sm">
        <Button
          onClick={() => dialogStore?.closeDialog?.('dialog-resource-copy')}
          variant="subtle"
          color="blackroomGray"
          c={palette.blackroomDarkGray2}
        >
          {t('Common:cta.cancel')}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={loading}
          variant="filled"
          loading={loading}
          color="blackroomBlue"
        >
          {t('Common:cta.validate')}
        </Button>
      </Group>
    </Flex>
  );
}

export default DialogResourceCopy;
